import { supabase } from './supabase';

export interface CopyGanttColorsResult {
  success: boolean;
  matchedCount: number;
  unmatchedCount: number;
  updatedCount: number;
  message: string;
  details: Array<{
    targetItemId: string;
    targetTitle: string;
    matchedBy: 'task_no' | 'title' | 'sort_order' | null;
    sourceTitle: string | null;
    oldColor: string | null;
    newColor: string | null;
  }>;
}

const METHUEN_PROJECT_ID = 'e55fe153-7c90-4ce0-9ccb-eb3a2ba88235';

/**
 * Copy Gantt bar colors from 5 Methuen Road to a target project.
 * Only updates `bar_color` and `bar_color_mode` — never touches dates,
 * progress, completion, or any other fields.
 *
 * Matching priority:
 * 1. task_no (exact)
 * 2. title (case-insensitive, trimmed)
 * 3. sort_order (exact)
 */
export async function copyGanttColorsFromMethuen(
  targetProjectId: string
): Promise<CopyGanttColorsResult> {
  if (!targetProjectId) {
    return {
      success: false,
      matchedCount: 0,
      unmatchedCount: 0,
      updatedCount: 0,
      message: 'Target project ID is required.',
      details: [],
    };
  }

  try {
    // Load source items from 5 Methuen Road
    const { data: sourceItems, error: sourceErr } = await supabase
      .from('jphph_project_schedule_items')
      .select(
        'id, task_no, title, sort_order, gantt_color'
      )
      .eq('project_id', METHUEN_PROJECT_ID)
      .or('is_deleted.eq.false,is_deleted.is.null')
      .order('sort_order', { ascending: true });

    if (sourceErr) {
      console.error('[CopyGanttColors] Source query error:', sourceErr);
      return {
        success: false,
        matchedCount: 0,
        unmatchedCount: 0,
        updatedCount: 0,
        message: `Failed to load 5 Methuen Road schedule items: ${sourceErr.message}`,
        details: [],
      };
    }

    if (!sourceItems || sourceItems.length === 0) {
      return {
        success: false,
        matchedCount: 0,
        unmatchedCount: 0,
        updatedCount: 0,
        message: 'No schedule items found in 5 Methuen Road.',
        details: [],
      };
    }

    // Load target items
    const { data: targetItems, error: targetErr } = await supabase
      .from('jphph_project_schedule_items')
      .select(
        'id, task_no, title, sort_order, gantt_color'
      )
      .eq('project_id', targetProjectId)
      .or('is_deleted.eq.false,is_deleted.is.null')
      .order('sort_order', { ascending: true });

    if (targetErr) {
      console.error('[CopyGanttColors] Target query error:', targetErr);
      return {
        success: false,
        matchedCount: 0,
        unmatchedCount: 0,
        updatedCount: 0,
        message: `Failed to load target project schedule items: ${targetErr.message}`,
        details: [],
      };
    }

    if (!targetItems || targetItems.length === 0) {
      return {
        success: false,
        matchedCount: 0,
        unmatchedCount: 0,
        updatedCount: 0,
        message: 'Target project has no schedule items.',
        details: [],
      };
    }

    // Build lookup indexes for source items
    const sourceByTaskNo = new Map<number, typeof sourceItems[0]>();
    const sourceByTitle = new Map<string, typeof sourceItems[0]>();
    const sourceBySortOrder = new Map<number, typeof sourceItems[0]>();

    for (const item of sourceItems) {
      if (item.task_no != null) sourceByTaskNo.set(item.task_no, item);
      const normTitle = (item.title || '').trim().toLowerCase();
      if (normTitle) sourceByTitle.set(normTitle, item);
      if (item.sort_order != null) sourceBySortOrder.set(item.sort_order, item);
    }

    const updates: Array<{
      id: string;
      gantt_color: string | null;
    }> = [];

    const details: CopyGanttColorsResult['details'] = [];
    let matchedCount = 0;
    let unmatchedCount = 0;

    for (const target of targetItems) {
      let source: typeof sourceItems[0] | undefined;
      let matchedBy: 'task_no' | 'title' | 'sort_order' | null = null;

      // 1. Match by task_no
      if (target.task_no != null) {
        source = sourceByTaskNo.get(target.task_no);
        if (source) matchedBy = 'task_no';
      }

      // 2. Match by title (case-insensitive trimmed)
      if (!source) {
        const normTitle = (target.title || '').trim().toLowerCase();
        if (normTitle) {
          source = sourceByTitle.get(normTitle);
          if (source) matchedBy = 'title';
        }
      }

      // 3. Match by sort_order
      if (!source && target.sort_order != null) {
        source = sourceBySortOrder.get(target.sort_order);
        if (source) matchedBy = 'sort_order';
      }

      if (source && source.gantt_color) {
        matchedCount++;
        const needsUpdate = target.gantt_color !== source.gantt_color;

        details.push({
          targetItemId: target.id,
          targetTitle: target.title || '',
          matchedBy,
          sourceTitle: source.title || null,
          oldColor: target.gantt_color,
          newColor: source.gantt_color,
        });

        if (needsUpdate) {
          updates.push({
            id: target.id,
            gantt_color: source.gantt_color,
          });
        }
      } else if (source) {
        // Matched but source has no color — count as matched, no update needed
        matchedCount++;
        details.push({
          targetItemId: target.id,
          targetTitle: target.title || '',
          matchedBy,
          sourceTitle: source.title || null,
          oldColor: target.gantt_color,
          newColor: source.gantt_color,
        });
      } else {
        unmatchedCount++;
        details.push({
          targetItemId: target.id,
          targetTitle: target.title || '',
          matchedBy: null,
          sourceTitle: null,
          oldColor: target.gantt_color,
          newColor: null,
        });
      }
    }

    // Batch update in chunks of 50
    const CHUNK = 50;
    let updatedCount = 0;
    const updateErrors: string[] = [];

    for (let i = 0; i < updates.length; i += CHUNK) {
      const chunk = updates.slice(i, i + CHUNK);
      const updateResults = await Promise.all(
        chunk.map((u) =>
          supabase
            .from('jphph_project_schedule_items')
            .update({
              gantt_color: u.gantt_color,
              updated_at: new Date().toISOString(),
            })
            .eq('id', u.id)
            .eq('project_id', targetProjectId)
        )
      );

      for (let j = 0; j < updateResults.length; j++) {
        if (updateResults[j].error) {
          updateErrors.push(`${chunk[j].id}: ${updateResults[j].error!.message}`);
        } else {
          updatedCount++;
        }
      }
    }

    if (updateErrors.length > 0) {
      console.error('[CopyGanttColors] Update errors:', updateErrors);
    }

    const parts: string[] = [];
    if (matchedCount > 0) parts.push(`${matchedCount} matched`);
    if (updatedCount > 0) parts.push(`${updatedCount} updated`);
    if (unmatchedCount > 0) parts.push(`${unmatchedCount} unmatched`);
    if (updateErrors.length > 0) parts.push(`${updateErrors.length} failed`);

    const message = parts.length > 0
      ? `Color copy complete: ${parts.join(', ')}.`
      : 'No colors needed to be copied.';

    console.log('[CopyGanttColors] Result', {
      targetProjectId,
      sourceItemCount: sourceItems.length,
      targetItemCount: targetItems.length,
      matchedCount,
      updatedCount,
      unmatchedCount,
      errors: updateErrors.length,
    });

    return {
      success: updateErrors.length === 0 || updatedCount > 0,
      matchedCount,
      unmatchedCount,
      updatedCount,
      message,
      details,
    };
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[CopyGanttColors] Unexpected error:', msg);
    return {
      success: false,
      matchedCount: 0,
      unmatchedCount: 0,
      updatedCount: 0,
      message: `Color copy failed unexpectedly: ${msg}`,
      details: [],
    };
  }
}